import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Accordion, AccordionVariants } from '@components/atoms/Accordion';
import { OrderStateBadge } from '@components/molecules/badges/OrderStateBadge';
import { Button, ButtonMode } from '@components/atoms/Button';
import { OrderDetailAccordionButton } from '@components/molecules/OrderDetailAccordionButton';
import { OrderHistoryModal } from '@components/molecules/OrderHistoryModal';
import { OrderHistoryTable } from '@components/molecules/OrderHistoryTable';
import { OrderProductsTable } from '@components/molecules/OrderProductsTable';
import { DeliveryTable, PaymentsTable } from './tables';
import { useGetMyOrderDetail } from '@services/useGetMyOrderDetail';
import { useCancelOrder } from '@services/useCancelOrder';
import { OrderDeliveryKeys, OrderState } from '@types';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { formatDate, formatPrice, formatTime } from '@utils/format';
import styles from './MyOrderDetail.module.css';
import { OrderBillingTable } from '@components/molecules/OrderBillingTable';
import { useConfirmStoreOrderDelivery } from '@services/useConfirmStoreOrderDelivery';
import { ManagementCard } from '@components/molecules/ManagementCard';

export const MyOrderDetail: FC = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const confirmReception = params.get('confirmReception');
    const { uuid } = useParams();
    const { showDefaultNotification } = useNotifications();
    const { myOrderDetail, isLoadingMyOrderDetail } = useData();
    const { request: requestMyOrderDetail } = useGetMyOrderDetail();
    const {
        request: requestConfirmStoreOrderDelivery,
        lastResponseTime: lastConfirm,
        errorMessage: errorConfirm,
        success: successConfirm,
    } = useConfirmStoreOrderDelivery();
    const {
        request: requestCancelOrder,
        success: successCancel,
        errorMessage: errorCancel,
        lastResponseTime: lastCancel,
    } = useCancelOrder();
    const { t } = useTranslation();

    const translations = {
        title: t('my-order-detail-component.title'),
        actions: t('my-order-detail-component.actions.label'),
        cancel: t('my-order-detail-component.actions.cancel-order'),
        productsTitle: t('my-order-detail-component.products.title'),
        billingTitle: t('my-order-detail-component.billing.title'),
        deliveryTitle: t('my-order-detail-component.delivery.title'),
        paymentsTitle: t('my-order-detail-component.payments.title'),
        historyTitle: t('my-order-detail-component.history.title'),
        orderTitle: t('my-order-detail-component.order-card.title'),
        client: t('my-order-detail-component.order-card.client'),
        date: t('my-order-detail-component.order-card.date'),
        total: t('my-order-detail-component.order-card.total'),
        discount: t('my-order-detail-component.order-card.discount'),
        addressLabel: t('my-order-detail-component.delivery-card.address.label'),
        deliveryDigital: t('my-order-detail-component.delivery-card.address.digital'),
        deliveryAtAddress: t('my-order-detail-component.delivery-card.address.at-address'),
        deliveryAtStore: t('my-order-detail-component.delivery-card.address.at-store'),
        deliveryAtPickupSpot: t('my-order-detail-component.delivery-card.address.at-pickup-spot'),
        deliveryAtLocker: t('my-order-detail-component.delivery-card.address.at-locker'),
        // delivery: t('my-order-detail-component.delivery-card.delivery'),
        condition: t('my-order-detail-component.delivery-card.condition'),
    };

    // make sure confirm service is called only once
    const confirmedRef = useRef<boolean>(false);

    useEffect(() => {
        if (confirmReception && !confirmedRef.current) {
            confirmedRef.current = true;
            requestConfirmStoreOrderDelivery({ uuid: confirmReception });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmReception]);

    // fetch order detail info
    useEffect(() => {
        requestMyOrderDetail({ uuid: uuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [viewHistoryModal, setViewHistoryModal] = useState<{ logId: string | null; show: boolean }>({
        logId: null,
        show: false,
    });

    const deliveryTranslations: Record<OrderDeliveryKeys, string> = {
        locker: translations.deliveryAtLocker,
        shipToPickupSpot: translations.deliveryAtPickupSpot,
        pickupAtStore: translations.deliveryAtStore,
        shipToAddress: translations.deliveryAtAddress,
        digitalDelivery: translations.deliveryDigital,
    };

    const deliveryKey = Object.keys(deliveryTranslations).find((key) => {
        const value = myOrderDetail?.data?.delivery?.[key as OrderDeliveryKeys];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return Boolean(value);
    });

    const deliveryType = deliveryKey ? deliveryTranslations[deliveryKey as OrderDeliveryKeys] : '';

    useEffect(() => {
        if (lastConfirm) {
            if (errorConfirm) {
                showDefaultNotification({ type: 'error', message: errorConfirm });
            } else {
                showDefaultNotification({ type: 'success', message: successConfirm || '' });
                requestMyOrderDetail({ uuid: uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastConfirm]);

    useEffect(() => {
        if (lastCancel) {
            if (errorCancel) {
                showDefaultNotification({ type: 'error', message: errorCancel });
            } else {
                showDefaultNotification({ type: 'success', message: successCancel || '' });
                requestMyOrderDetail({ uuid: uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCancel]);

    const billingAddress = myOrderDetail?.data?.billingAddress;

    const billingAddressText = billingAddress
        ? `${billingAddress?.recipient_first_name} ${billingAddress?.recipient_last_name}` +
          `\nNIF: ${billingAddress?.fiscal_number || '-'}` +
          `\n${billingAddress.address_line1}` +
          `\n${billingAddress.address_line2}` +
          `\n${billingAddress.postal_code}` +
          `\n${billingAddress.municipality}` +
          `\n${billingAddress.region?.name}`
        : '';

    return (
        <MyAccountContainer
            title={translations.title}
            overrideOnDesktop={
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <Typography classes="mb-2" color="muted" fontSize="16">
                        <>
                            {translations.title} / {!isLoadingMyOrderDetail ? myOrderDetail?.data?.client : ''} /{' '}
                            <Typography tagVariant={TypographyTagVariants.span} fontWeight="800">
                                {!isLoadingMyOrderDetail ? myOrderDetail?.data?.order_code : ''}
                            </Typography>
                        </>
                    </Typography>

                    {!isLoadingMyOrderDetail && myOrderDetail?.data?.status === 'pending' && (
                        <Button
                            mode={ButtonMode.primary}
                            label={translations.actions}
                            dropdown={{
                                side: 'right',
                                options: [
                                    {
                                        label: translations.cancel,
                                        icon: 'fa-light fa-xmark',
                                        onClick: () => requestCancelOrder({ uuid: uuid }),
                                    },
                                ],
                            }}
                        />
                    )}
                </div>
            }>
            <>
                {!isLoadingMyOrderDetail ? (
                    <>
                        <div className="row g-4 mt-4 mb-4" data-testid="order-management">
                            <div className="col-12 col-md-6">
                                <ManagementCard
                                    title={`${translations.orderTitle} - ${myOrderDetail?.data?.order_code || ''}`}
                                    content={[
                                        { label: translations.client, content: myOrderDetail?.data?.client || '' },
                                        {
                                            label: translations.date,
                                            content: myOrderDetail?.data?.creation_date ? (
                                                <>
                                                    {formatDate(myOrderDetail.data.creation_date)}
                                                    <Typography tagVariant={TypographyTagVariants.span} classes="text-muted ms-2">
                                                        <>({formatTime(myOrderDetail?.data?.creation_date)})</>
                                                    </Typography>
                                                </>
                                            ) : (
                                                <></>
                                            ),
                                        },
                                        { label: translations.total, content: formatPrice(myOrderDetail?.data?.total) },
                                        myOrderDetail?.data?.discount
                                            ? {
                                                  label: translations.discount,
                                                  content: formatPrice(myOrderDetail?.data?.discount),
                                              }
                                            : undefined,
                                        {
                                            label: translations.condition,
                                            content: (
                                                <OrderStateBadge
                                                    state={OrderState[myOrderDetail?.data?.status as keyof typeof OrderState]}
                                                />
                                            ),
                                        },
                                    ].filter((item) => item !== undefined)}
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <ManagementCard
                                    title={translations.deliveryTitle}
                                    content={[{ label: translations.addressLabel, content: deliveryType }]}
                                />
                            </div>
                        </div>

                        <Accordion
                            variant={AccordionVariants.variant2}
                            items={[
                                {
                                    id: 'collapseProducts',
                                    buttonContent: (
                                        <OrderDetailAccordionButton
                                            title={translations.productsTitle}
                                            itemsCount={myOrderDetail?.data?.nProducts}
                                            total={myOrderDetail?.data?.productsValue}
                                        />
                                    ),
                                    bodyContent: (
                                        <OrderProductsTable
                                            products={
                                                myOrderDetail?.data?.products?.map((item) => {
                                                    return {
                                                        uuid: item.uuid || '',
                                                        name: item.name || '',
                                                        attributes: item.attributes || [],
                                                        quantity: item.quantity || 0,
                                                        price: item.price || 0,
                                                        rating: item.rating || 0,
                                                    };
                                                }) || []
                                            }
                                            withRatings={myOrderDetail?.data?.status === 'delivered'}
                                        />
                                    ),
                                },
                                /* #TODO: bills
                                {
                                    id: 'collapseBills',
                                    buttonContent: (
                                        <OrderDetailAccordionButton
                                            title={translations.billingTitle}
                                            itemsCount={bills.list.length}
                                            total={bills.total}
                                        />
                                    ),
                                    bodyContent: <BillingTable bills={bills.list} showEditModal={() => {}} />,
                                },*/ {
                                    id: 'collapseDelivery',
                                    buttonContent: (
                                        <OrderDetailAccordionButton
                                            title={translations.deliveryTitle}
                                            total={myOrderDetail?.data?.shipping}
                                        />
                                    ),
                                    bodyContent: <DeliveryTable />,
                                },
                                {
                                    id: 'collapseBilling',
                                    buttonContent: <OrderDetailAccordionButton title={translations.billingTitle} />,
                                    bodyContent: <OrderBillingTable address={billingAddressText} />,
                                },
                                {
                                    id: 'collapsePayments',
                                    buttonContent: (
                                        <OrderDetailAccordionButton
                                            title={translations.paymentsTitle}
                                            total={myOrderDetail?.data?.total}
                                        />
                                    ),
                                    buttonOptions: (
                                        <>
                                            {myOrderDetail?.data?.paymentMethod?.image?.src && (
                                                <img
                                                    src={myOrderDetail?.data?.paymentMethod?.image?.src}
                                                    alt={myOrderDetail?.data?.paymentMethod?.name}
                                                />
                                            )}
                                        </>
                                    ),
                                    bodyContent: (
                                        <PaymentsTable
                                            name={myOrderDetail?.data?.paymentMethod?.name || ''}
                                            method={myOrderDetail?.data?.paymentMethod?.method || ''}
                                            multibancoEntity={myOrderDetail?.data?.paymentMethod?.multibancoEntity || ''}
                                            multibancoReference={myOrderDetail?.data?.paymentMethod?.multibancoReference || ''}
                                            phoneNumber={myOrderDetail?.data?.paymentMethod?.paymentPhoneNumber}
                                        />
                                    ),
                                },
                                {
                                    id: 'collapseHistory',
                                    buttonContent: <OrderDetailAccordionButton title={translations.historyTitle} />,
                                    buttonOptions: (
                                        <>
                                            {/* view all logs */}
                                            <button
                                                className="text-muted"
                                                onClick={() => setViewHistoryModal({ logId: null, show: true })}>
                                                <i className="fa-light fa-clock-rotate-left" />
                                            </button>
                                        </>
                                    ),
                                    bodyContent: (
                                        <OrderHistoryTable
                                            logs={
                                                myOrderDetail?.data?.logs?.map((item, index) => {
                                                    return {
                                                        id: `${index}`,
                                                        date: item.datetime || '',
                                                        amount: `${item.amount || ''}`,
                                                        notes: item.description || '',
                                                        store: item.store || '',
                                                    };
                                                }) || []
                                            }
                                            showViewModal={(id) => setViewHistoryModal({ logId: id, show: true })}
                                        />
                                    ),
                                },
                            ]}
                            customClasses={{
                                accordion: styles.accordion,
                            }}
                        />

                        <OrderHistoryModal
                            logId={viewHistoryModal.logId}
                            logs={
                                myOrderDetail?.data?.logs?.map((item, index) => {
                                    return {
                                        id: `${index}`,
                                        date: item.datetime || '',
                                        amount: `${item.amount || ''}`,
                                        notes: item.description || '',
                                        store: item.store || '',
                                    };
                                }) || []
                            }
                            show={viewHistoryModal.show}
                            onClose={() => setViewHistoryModal({ logId: null, show: false })}
                        />
                    </>
                ) : (
                    <></>
                )}
            </>
        </MyAccountContainer>
    );
};
